import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import IMAGES from '../../data/images';
import { lockBodyScroll, unlockBodyScroll } from '../../utils/scrollLock';
import Button from './ui/Button';

const SLIDES = [
  {
    image: IMAGES.welcome[0],
    title: "Welcome to Mums Connect",
    body: "You're not alone in this. Whether you're expecting, a new mum, or just looking for your people — we're so glad you're here.",
  },
  {
    image: IMAGES.welcome[1],
    title: "Find your village",
    body: "Discover playgroups, coffee catchups, walks, and workshops happening near you. No pressure, just connection.",
  },
  {
    image: IMAGES.welcome[2],
    title: "You belong here",
    body: "Share your wins, your hard days, or just say hi in our community feed. This is your space.",
  },
];

function WelcomeModal({ user }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (user && !localStorage.getItem('mc_welcomed')) {
      // Small delay so the app loads first
      const timer = setTimeout(() => setIsOpen(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  useEffect(() => {
    if (isOpen) {
      lockBodyScroll();
    }

    return () => {
      if (isOpen) {
        unlockBodyScroll();
      }
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('mc_welcomed', 'true');
  };

  const handleNext = () => {
    if (currentSlide < SLIDES.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      handleClose();
    }
  };

  const slide = SLIDES[currentSlide];
  const isLast = currentSlide === SLIDES.length - 1;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-onboarding"
            onClick={handleClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', stiffness: 260, damping: 24 }}
            className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 z-onboarding-content w-auto md:w-full md:max-w-md flex items-center justify-center"
          >
            <div className="bg-white rounded-3xl overflow-hidden shadow-2xl w-full max-h-[90vh]">
              {/* Image */}
              <div className="relative h-48 md:h-56 overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentSlide}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.3 }}
                    src={slide.image}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
                
                {/* Skip button */}
                <button
                  onClick={handleClose}
                  className="absolute top-3 right-3 text-xs font-medium text-white/80 bg-black/20 backdrop-blur-sm px-3 py-1.5 rounded-full hover:bg-black/30 transition-colors"
                >
                  Skip
                </button>
              </div>

              {/* Content */}
              <div className="px-6 pb-6 pt-2 text-center">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 className="text-2xl font-headline text-stone-800 mb-3 italic">{slide.title}</h2>
                    <p className="text-stone-500 text-sm leading-relaxed mb-6">{slide.body}</p>
                  </motion.div>
                </AnimatePresence>

                {/* Dots */}
                <div className="flex items-center justify-center gap-2 mb-5">
                  {SLIDES.map((_, idx) => (
                    <div
                      key={idx}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        idx === currentSlide ? 'w-6 bg-primary' : 'w-1.5 bg-stone-200'
                      }`}
                    />
                  ))}
                </div>

                {/* CTA */}
                <Button as={motion.button} whileTap={{ scale: 0.96 }} onClick={handleNext} size="lg" className="text-sm">
                  {isLast ? "Let's Go" : "Next"}
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default WelcomeModal;
